import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AiAutoFillSection } from "../../../../components/AiAutoFillSection";
import type { CrmParseMatterResult } from "../../../../hooks/use-crm-parse-text-api";
import { useCrmParseText } from "../../../../hooks/use-crm-parse-text-api";
import type { FocusMatterItem } from "../../../../hooks/use-focus-matters-api";

type FocusMatterFormData = {
  title: string;
  clientCompany: string;
  rewardMin: string;
  rewardMax: string;
  nearestStation: string;
  remoteType: string;
  deadline: string;
  requiredSkills: string;
  niceToHaveSkills: string;
  notes: string;
};

const INITIAL_FORM: FocusMatterFormData = {
  title: "",
  clientCompany: "",
  rewardMin: "",
  rewardMax: "",
  nearestStation: "",
  remoteType: "",
  deadline: "",
  requiredSkills: "",
  niceToHaveSkills: "",
  notes: "",
};

type FocusMatterFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  submitting: boolean;
  initialData?: FocusMatterItem;
};

/**
 * 注力案件の作成・編集モーダル
 * @param {object} props コンポーネントプロパティ
 * @param {boolean} props.isOpen モーダルの表示状態
 * @param {Function} props.onClose 閉じるコールバック
 * @param {Function} props.onSubmit 送信コールバック
 * @param {boolean} props.submitting 送信中フラグ
 * @param {FocusMatterItem} [props.initialData] 編集時の既存データ
 * @return {React.ReactElement | null} モーダル要素
 */
export const FocusMatterFormModal: React.FC<FocusMatterFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  submitting,
  initialData,
}) => {
  const isEditMode = !!initialData;
  const { parseMatterText, loading: isParsing } = useCrmParseText();
  const [form, setForm] = useState<FocusMatterFormData>(INITIAL_FORM);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setForm({
          title: initialData.title,
          clientCompany: initialData.clientCompany ?? "",
          rewardMin:
            initialData.rewardMin != null
              ? String(initialData.rewardMin / 10000)
              : "",
          rewardMax:
            initialData.rewardMax != null
              ? String(initialData.rewardMax / 10000)
              : "",
          nearestStation: initialData.nearestStation ?? "",
          remoteType: initialData.remoteType ?? "",
          deadline: initialData.deadline ?? "",
          requiredSkills: initialData.requiredSkills?.join(", ") ?? "",
          niceToHaveSkills: initialData.niceToHaveSkills?.join(", ") ?? "",
          notes: initialData.notes ?? "",
        });
      } else {
        setForm(INITIAL_FORM);
      }
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, initialData]);

  const handleChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      const { name, value } = e.target;
      setForm(prev => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleAiParse = useCallback(
    async (text: string) => {
      const result: CrmParseMatterResult = await parseMatterText(text);
      setForm(prev => ({
        ...prev,
        title: result.title ?? prev.title,
        clientCompany: result.clientCompany ?? prev.clientCompany,
        rewardMin:
          result.rewardMin != null ? String(result.rewardMin) : prev.rewardMin,
        rewardMax:
          result.rewardMax != null ? String(result.rewardMax) : prev.rewardMax,
        nearestStation: result.nearestStation ?? prev.nearestStation,
        remoteType: result.remoteType ?? prev.remoteType,
        deadline: result.deadline ?? prev.deadline,
        requiredSkills: result.requiredSkills ?? prev.requiredSkills,
        niceToHaveSkills: result.niceToHaveSkills ?? prev.niceToHaveSkills,
        notes: result.notes ?? prev.notes,
      }));
      toast.success("AIによる自動入力が完了しました");
    },
    [parseMatterText],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!form.title.trim()) return;
      const parseSkills = (text: string): string[] | undefined => {
        if (!text.trim()) return undefined;
        return text
          .split(/[,、\s]+/)
          .map(s => s.trim())
          .filter(Boolean);
      };
      await onSubmit({
        title: form.title,
        clientCompany: form.clientCompany || undefined,
        rewardMin: form.rewardMin
          ? Math.round(Number(form.rewardMin) * 10000)
          : undefined,
        rewardMax: form.rewardMax
          ? Math.round(Number(form.rewardMax) * 10000)
          : undefined,
        nearestStation: form.nearestStation || undefined,
        remoteType: form.remoteType || undefined,
        deadline: form.deadline || undefined,
        requiredSkills: parseSkills(form.requiredSkills),
        niceToHaveSkills: parseSkills(form.niceToHaveSkills),
        notes: form.notes || undefined,
      });
    },
    [form, onSubmit],
  );

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box w-11/12 max-w-lg">
        <h3 className="font-bold text-lg mb-4">
          {isEditMode ? "注力案件を編集" : "注力案件を追加"}
        </h3>

        {!isEditMode && (
          <AiAutoFillSection
            loading={isParsing}
            onParse={handleAiParse}
            placeholder={
              "案件情報のテキストを貼り付けて「AIで自動入力」を押してください。\n案件タイトル・単価・スキル・勤務地などが自動で入力されます。"
            }
          />
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-control mb-3">
            <label className="label" htmlFor="fm-title">
              <span className="label-text">
                案件タイトル <span className="text-error">*</span>
              </span>
            </label>
            <input
              id="fm-title"
              name="title"
              type="text"
              className="input input-bordered input-sm"
              value={form.title}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-control mb-3">
            <label className="label" htmlFor="fm-clientCompany">
              <span className="label-text">発注元企業</span>
            </label>
            <input
              id="fm-clientCompany"
              name="clientCompany"
              type="text"
              className="input input-bordered input-sm"
              value={form.clientCompany}
              onChange={handleChange}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="form-control">
              <label className="label" htmlFor="fm-rewardMin">
                <span className="label-text">単価下限（万円）</span>
              </label>
              <input
                id="fm-rewardMin"
                name="rewardMin"
                type="number"
                className="input input-bordered input-sm"
                value={form.rewardMin}
                onChange={handleChange}
                min={0}
              />
            </div>
            <div className="form-control">
              <label className="label" htmlFor="fm-rewardMax">
                <span className="label-text">単価上限（万円）</span>
              </label>
              <input
                id="fm-rewardMax"
                name="rewardMax"
                type="number"
                className="input input-bordered input-sm"
                value={form.rewardMax}
                onChange={handleChange}
                min={0}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="form-control">
              <label className="label" htmlFor="fm-nearestStation">
                <span className="label-text">勤務地</span>
              </label>
              <input
                id="fm-nearestStation"
                name="nearestStation"
                type="text"
                className="input input-bordered input-sm"
                value={form.nearestStation}
                onChange={handleChange}
              />
            </div>
            <div className="form-control">
              <label className="label" htmlFor="fm-remoteType">
                <span className="label-text">リモート</span>
              </label>
              <select
                id="fm-remoteType"
                name="remoteType"
                className="select select-bordered select-sm"
                value={form.remoteType}
                onChange={handleChange}
              >
                <option value="">未指定</option>
                <option value="full_remote">フルリモート</option>
                <option value="hybrid">ハイブリッド</option>
                <option value="onsite_only">出社</option>
              </select>
            </div>
          </div>

          <div className="form-control mb-3">
            <label className="label" htmlFor="fm-deadline">
              <span className="label-text">募集期限</span>
            </label>
            <input
              id="fm-deadline"
              name="deadline"
              type="date"
              className="input input-bordered input-sm"
              value={form.deadline}
              onChange={handleChange}
            />
          </div>

          <div className="form-control mb-3">
            <label className="label" htmlFor="fm-requiredSkills">
              <span className="label-text">必須スキル（カンマ区切り）</span>
            </label>
            <input
              id="fm-requiredSkills"
              name="requiredSkills"
              type="text"
              className="input input-bordered input-sm"
              placeholder="Java, Spring Boot, AWS"
              value={form.requiredSkills}
              onChange={handleChange}
            />
          </div>

          <div className="form-control mb-3">
            <label className="label" htmlFor="fm-niceToHaveSkills">
              <span className="label-text">歓迎スキル（カンマ区切り）</span>
            </label>
            <input
              id="fm-niceToHaveSkills"
              name="niceToHaveSkills"
              type="text"
              className="input input-bordered input-sm"
              placeholder="Docker, Kubernetes"
              value={form.niceToHaveSkills}
              onChange={handleChange}
            />
          </div>

          <div className="form-control mb-4">
            <label className="label" htmlFor="fm-notes">
              <span className="label-text">メモ</span>
            </label>
            <textarea
              id="fm-notes"
              name="notes"
              className="textarea textarea-bordered textarea-sm"
              rows={2}
              value={form.notes}
              onChange={handleChange}
            />
          </div>

          <div className="modal-action">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={onClose}
              disabled={submitting}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={submitting || !form.title.trim()}
            >
              {submitting ? (
                <span className="loading loading-spinner loading-xs" />
              ) : isEditMode ? (
                "保存"
              ) : (
                "追加"
              )}
            </button>
          </div>
        </form>
      </div>
      <div
        className="modal-backdrop"
        onClick={() => !submitting && onClose()}
      />
    </div>
  );
};
